import type { EstimateFile } from "@/enums/estimate-file";
import type { File as EstimateFileType, UploadedFileProperty } from "@/interfaces/estimation.interface";
import estimateProvider, { type ValidateUploadedPdfFormatResponse } from "@/providers/estimate.provider";
import FileProvider from "@/providers/file.provider";
import { appSelector } from "@/redux/selectors/app.selector";
import { useSelector } from "@/redux/store";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

export interface UploadProgress {
  [key: string]: {
    progress: number;
    fileName: string;
    fileType: EstimateFile;
  };
}

export interface PdfValidationState {
  [uploadId: string]: {
    status: "VALIDATING" | "CLASSIFIED" | "ERROR";
    format?: ValidateUploadedPdfFormatResponse["format"];
    error?: string;
  };
}

export function useUploadEstimationFiles(currentEstimateID: string) {
  const user = useSelector(appSelector.user);

  const [uploadedFiles, setUploadedFiles] = useState<EstimateFileType[]>([]);
  const [uploadResults, setUploadResults] = useState<UploadedFileProperty[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [pdfValidationState, setPdfValidationState] = useState<PdfValidationState>({});
  const [isUploading, setIsUploading] = useState(false);
  const cancelledUploadIdsRef = useRef<Set<string>>(new Set());

  const isEligiblePdfUpload = useCallback((file: File, remotePath?: string) => {
    const lowerFileName = file.name.toLowerCase();
    const lowerRemotePath = remotePath?.toLowerCase() ?? "";
    return lowerFileName.endsWith(".pdf") || lowerRemotePath.endsWith(".pdf");
  }, []);

  const validateUploadedPdf = useCallback(async (uploadId: string, remotePath?: string) => {
    if (!remotePath || cancelledUploadIdsRef.current.has(uploadId)) {
      return;
    }

    setPdfValidationState((prev) => ({
      ...prev,
      [uploadId]: { status: "VALIDATING" },
    }));

    try {
      if (cancelledUploadIdsRef.current.has(uploadId)) {
        return;
      }

      const validationPromise = estimateProvider.validateUploadedPdfFormat({ remotePath });
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("PDF validation timed out")), 20_000);
      });
      const validation = await Promise.race([validationPromise, timeoutPromise]);

      if (cancelledUploadIdsRef.current.has(uploadId)) {
        return;
      }

      setPdfValidationState((prev) => ({
        ...prev,
        [uploadId]: { status: "CLASSIFIED", format: validation.format },
      }));
    } catch (error) {
      if (cancelledUploadIdsRef.current.has(uploadId)) {
        return;
      }

      setPdfValidationState((prev) => ({
        ...prev,
        [uploadId]: { status: "ERROR", error: (error as Error)?.message ?? "Failed to validate pdf format" },
      }));
    }
  }, []);

  const uploadMultipleFiles = useCallback(
    async (files: File[], fileType: EstimateFile, postUploadCommand: string, tpa?: string, insuranceCarrier?: string) => {
      setIsUploading(true);
      const uploadBy = user?.lassoUserId ?? "";

      await Promise.all(
        files.map(async (file) => {
          const uploadId = uuidv4();

          setUploadProgress((prev) => ({
            ...prev,
            [uploadId]: { progress: 0, fileName: file.name, fileType },
          }));

          let progress = 0;
          const interval = setInterval(() => {
            progress += Math.random() * 5 + 3;
            if (progress >= 90) {
              clearInterval(interval);
              setUploadProgress((prev) => ({
                ...prev,
                [uploadId]: { progress: Math.min(progress, 90), fileName: file.name, fileType },
              }));
            } else {
              setUploadProgress((prev) => ({
                ...prev,
                [uploadId]: { progress: Math.min(progress, 90), fileName: file.name, fileType },
              }));
            }
          }, 700);

          try {
            const response = await FileProvider.uploadFile(file, uploadId, currentEstimateID, postUploadCommand, tpa, insuranceCarrier);

            clearInterval(interval);

            setUploadProgress((prev) => {
              const newProgress = { ...prev };
              delete newProgress[uploadId];
              return newProgress;
            });

            if (response) {
              const remotePath = response[0]?.storagePath;
              setUploadedFiles((prev) => [
                ...prev,
                {
                  uploadId,
                  originalname: file.name,
                  uploadedSofar: file.size,
                  size: file.size,
                  fileType,
                  uploadDate: new Date().toLocaleDateString("en-US"),
                  uploadBy,
                  remotePath,
                  mime: response[0]?.mime,
                },
              ]);
              setUploadResults((prev) => [...prev, response[0]]);
              const skipValidation = import.meta.env.VITE_DISABLE_MITIGATION_REPORT_VALIDATION === "true";
              if (remotePath && isEligiblePdfUpload(file, remotePath) && !skipValidation) {
                void validateUploadedPdf(uploadId, remotePath);
              }
              setIsUploading(false);
            } else {
              throw new Error(`Upload failed for ${file.name}`);
            }
          } catch (err) {
            clearInterval(interval);
            setIsUploading(false);
            setUploadProgress((prev) => {
              const newProgress = { ...prev };
              delete newProgress[uploadId];
              return newProgress;
            });
            console.error((err as Error).message);
          }
        }),
      );
    },
    [user?.lassoUserId, currentEstimateID, isEligiblePdfUpload, validateUploadedPdf],
  );

  const removeUploadedFile = useCallback((uploadId: string) => {
    cancelledUploadIdsRef.current.add(uploadId);
    setUploadedFiles((prev) => prev.filter((file) => file.uploadId !== uploadId));
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[uploadId];
      return newProgress;
    });
    setPdfValidationState((prev) => {
      const newState = { ...prev };
      delete newState[uploadId];
      return newState;
    });
  }, []);

  const reset = useCallback(() => {
    cancelledUploadIdsRef.current = new Set();
    setUploadedFiles([]);
    setUploadProgress({});
    setPdfValidationState({});
  }, []);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return {
    reset,
    uploadedFiles,
    uploadResults,
    uploadProgress,
    pdfValidationState,
    isUploading,
    removeUploadedFile,
    uploadMultipleFiles,
  };
}
